import React from "react";

const blue = "#0B47B3";
const text = "#111827";
const muted = "#65728a";

const styles = {
  shell: {
    width: "100%",
    maxWidth: 420,
    minHeight: 0,
    background: "#ffffff",
    borderRadius: 28,
    boxShadow: "0 18px 40px rgba(16,24,40,0.08)",
    color: text,
    display: "flex",
    flexDirection: "column",
    fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif",
    gap: 16,
    overflow: "hidden"
  },
  inner: { padding: "clamp(18px, 6vw, 28px) clamp(16px, 6vw, 28px) 0" },
  header: {
    minHeight: 112,
    textAlign: "center",
    paddingTop: 10,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    flexShrink: 0
  },
  logoMark: {
    width: 74,
    height: 74,
    borderRadius: 999,
    margin: "0 auto 8px",
    background: "conic-gradient(from 28deg, #123aa8 0 21%, transparent 21% 30%, #2f86dd 30% 74%, #123aa8 74% 100%)",
    position: "relative"
  },
  logoCut: { position: "absolute", inset: 16, background: "#fff", borderRadius: 999 },
  logoArrow: {
    position: "absolute",
    right: 7,
    top: 29,
    width: 0,
    height: 0,
    borderTop: "11px solid transparent",
    borderBottom: "11px solid transparent",
    borderLeft: "18px solid #123aa8"
  },
  brand: { margin: 0, fontSize: 24, fontWeight: 900, letterSpacing: 3, color: blue },
  subBrand: { marginTop: 2, color: blue, fontSize: 13, fontWeight: 900, letterSpacing: 8 },
  successBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    padding: "8px 16px",
    borderRadius: 999,
    background: "#eefbf2",
    border: "1px solid #bce8c8",
    color: "#0a8d32",
    fontSize: 15,
    fontWeight: 900,
    marginTop: 20
  },
  subtitle: { color: muted, fontSize: 14, marginTop: 12, lineHeight: 1.35 },
  summary: {
    marginTop: 22,
    border: "1px solid #e7ebf2",
    borderRadius: 14,
    padding: "20px 18px",
    boxShadow: "0 10px 25px rgba(15,23,42,0.04)",
    display: "grid",
    gridTemplateColumns: "1fr 48px 1fr",
    alignItems: "center",
    gap: 10
  },
  smallLabel: { color: muted, fontWeight: 800, fontSize: 15, marginBottom: 10 },
  paidAmount: { color: text, fontSize: 30, fontWeight: 900, lineHeight: 1 },
  receivedAmount: { color: blue, fontSize: 30, fontWeight: 900, lineHeight: 1 },
  flagBadge: {
    marginTop: 13,
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "7px 11px",
    borderRadius: 999,
    background: "#f4f6f9",
    color: text,
    fontWeight: 800
  },
  arrow: {
    width: 48,
    height: 48,
    borderRadius: 999,
    border: "1px solid #dbe2eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: text
  },
  card: {
    marginTop: 16,
    border: "1px solid #e7ebf2",
    borderRadius: 14,
    padding: 14,
    boxShadow: "0 10px 25px rgba(15,23,42,0.04)"
  },
  title: { color: blue, fontSize: 15, fontWeight: 900, display: "flex", alignItems: "center", gap: 10, marginBottom: 8 },
  row: { minHeight: 39, display: "grid", gridTemplateColumns: "30px 1fr auto", alignItems: "center", gap: 8, borderBottom: "1px dashed #e6eaf1" },
  rowLast: { borderBottom: "none" },
  label: { color: muted, fontSize: 13, fontWeight: 700 },
  value: { color: text, fontSize: 13, fontWeight: 800, textAlign: "right", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8 },
  copyBtn: {
    width: 25,
    height: 25,
    border: "none",
    background: "transparent",
    color: blue,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0
  },
  highlight: {
    marginTop: 16,
    padding: "18px 14px",
    borderRadius: 14,
    border: "1px solid #b8d2ff",
    background: "linear-gradient(180deg,#f7fbff,#ffffff)",
    textAlign: "center"
  },
  highlightLabel: { color: muted, fontWeight: 900, fontSize: 16 },
  highlightAmount: { marginTop: 7, color: blue, fontWeight: 900, fontSize: 42, lineHeight: 1 },
  thanks: {
    marginTop: 15,
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    color: blue,
    fontSize: 13,
    fontWeight: 800,
    border: "1px solid #c8daf8",
    borderRadius: 999,
    padding: "8px 14px",
    background: "#f9fcff"
  },
  verify: { marginTop: 18, display: "grid", gridTemplateColumns: "112px 1fr", gap: 16, alignItems: "center" },
  qrBox: { width: 112, height: 112, borderRadius: 12, border: "1px solid #e7ebf2", display: "flex", alignItems: "center", justifyContent: "center", background: "#fff" },
  verifyTitle: { color: blue, fontSize: 15, fontWeight: 900 },
  verifyText: { color: muted, fontSize: 13, lineHeight: 1.45, marginTop: 8 },
  verified: {
    marginTop: 13,
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    borderRadius: 999,
    padding: "8px 13px",
    color: "#087c66",
    background: "#eaf8f2",
    fontWeight: 900,
    fontSize: 13
  },
  trust: { borderTop: "1px solid #dfe6f0", marginTop: 16, paddingTop: 14, textAlign: "center", color: blue, fontWeight: 800, fontSize: 13 },
  partner: { color: muted, marginTop: 8, fontSize: 12 },
  footer: { marginTop: 12, background: blue, color: "#fff", padding: "13px 18px 17px", textAlign: "center", fontSize: 12, lineHeight: 1.8 }
};

function SvgIcon({ name, size = 18 }) {
  const common = { width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round", style: { display: "inline-block", verticalAlign: "-0.18em", flexShrink: 0 } };
  const paths = {
    check: <path d="M20 6 9 17l-5-5" />,
    copy: <><rect x="9" y="9" width="10" height="10" rx="2" /><rect x="5" y="5" width="10" height="10" rx="2" /></>,
    doc: <><path d="M6 3h9l3 3v15H6z" /><path d="M9 8h6M9 12h6M9 16h4" /></>,
    tx: <><circle cx="12" cy="12" r="9" /><path d="M8 12h8M13 8l4 4-4 4" /></>,
    date: <><rect x="4" y="5" width="16" height="15" rx="2" /><path d="M8 3v4M16 3v4M4 10h16" /></>,
    time: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
    user: <><path d="M20 21a8 8 0 0 0-16 0" /><circle cx="12" cy="8" r="4" /></>,
    card: <><rect x="3" y="6" width="18" height="12" rx="2" /><path d="M3 10h18" /></>,
    rate: <><path d="M7 7h10M7 17h10M8 7a5 5 0 0 1 0 10M16 7a5 5 0 0 0 0 10" /></>,
    money: <><circle cx="12" cy="12" r="9" /><path d="M12 7v10M9 10c0-1.3 1.1-2 3-2s3 .7 3 2-1.1 2-3 2-3 .7-3 2 1.1 2 3 2 3-.7 3-2" /></>,
    shield: <path d="M12 3 5 6v5c0 4.5 2.7 8 7 10 4.3-2 7-5.5 7-10V6z" />,
    lock: <><rect x="5" y="10" width="14" height="10" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></>,
    heart: <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z" />,
    arrow: <path d="M5 12h14M13 6l6 6-6 6" />
  };
  return <svg {...common}>{paths[name]}</svg>;
}

function CurrencyFlag({ currency, src }) {
  if (src) return <img src={src} alt="" style={{ width: 18, height: 12, borderRadius: 3 }} />;
  if (currency === "BDT") {
    return <span aria-hidden style={{ width: 18, height: 18, borderRadius: 999, background: "#006a4e", display: "inline-flex", alignItems: "center", justifyContent: "center" }}><span style={{ width: 10, height: 10, borderRadius: 999, background: "#f42a41" }} /></span>;
  }
  if (currency === "CNY") {
    return <span aria-hidden style={{ width: 18, height: 18, borderRadius: 999, background: "#de2910", color: "#ffde00", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 9 }}>★</span>;
  }
  return null;
}

function PaymentBrandIcon({ method }) {
  const label = String(method || "").toLowerCase();
  const knownLogos = [
    label.includes("bkash") && ["bKash", "https://cdn.simpleicons.org/bkash/e2136e"],
    label.includes("alipay") && ["Alipay", "https://cdn.simpleicons.org/alipay/1677ff"],
    label.includes("wechat") && ["WeChat Pay", "https://cdn.simpleicons.org/wechat/07c160"],
    label.includes("nagad") && ["Nagad", "https://cdn.simpleicons.org/nagad/f15a24"],
    label.includes("rocket") && ["Rocket", "https://cdn.simpleicons.org/rocket/7c3aed"]
  ].filter(Boolean);

  if (knownLogos.length) {
    return <span aria-label="Payment brand" style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
      {knownLogos.map(([name, src]) => <img key={name} src={src} crossOrigin="anonymous" alt={name} style={{ height: 19, width: "auto", maxWidth: 56, objectFit: "contain" }} />)}
    </span>;
  }
  if (label.includes("bank")) return <SvgIcon name="card" size={19} />;
  if (label.includes("bkash")) return <span aria-hidden style={{ color: "#e2136e", fontSize: 22, fontWeight: 900, transform: "skew(-12deg)" }}>➤</span>;
  if (label.includes("nagad")) return <span aria-hidden style={{ width: 20, height: 20, borderRadius: 999, background: "#f97316", color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 900 }}>N</span>;
  if (label.includes("rocket")) return <span aria-hidden style={{ width: 20, height: 20, borderRadius: 999, background: "#7c3aed", color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 900 }}>R</span>;
  return null;
}

function DetailRow({ icon, label, value, copyValue, last, children }) {
  return (
    <div style={{ ...styles.row, ...(last ? styles.rowLast : {}) }}>
      <div style={{ color: "#2170dc" }}><SvgIcon name={icon} /></div>
      <div style={styles.label}>{label}</div>
      <div style={styles.value}>
        {children || value}
        {copyValue && (
          <button type="button" style={styles.copyBtn} onClick={() => navigator.clipboard?.writeText(copyValue)} aria-label={`Copy ${label}`}>
            <SvgIcon name="copy" size={16} />
          </button>
        )}
      </div>
    </div>
  );
}

export default function ReceiptMobile(props) {
  const {
    brand = "Cavelen XDL",
    paidAmount,
    paidCurrency = "BDT",
    paidFlagSrc,
    receivedAmount,
    receivedCurrency = "CNY",
    receivedFlagSrc,
    referenceId,
    transactionId,
    date,
    time,
    customerName,
    paymentMethod,
    exchangeRate,
    serviceFee,
    totalPaid,
    verifyUrl
  } = props || {};

  const [brandTop, brandBottom = "XDL"] = brand.toUpperCase().split(" ");
  const qrSrc = verifyUrl ? `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(verifyUrl)}` : "";

  return (
    <article style={styles.shell} aria-label="Cavelen transaction receipt">
      <div style={styles.inner}>
        <header style={styles.header}>
          <h1 style={styles.brand}>{brandTop}</h1>
          <div style={styles.subBrand}>{brandBottom}</div>
          <div style={styles.successBadge}><SvgIcon name="check" size={17} /> Transaction Successful</div>
          <div style={styles.subtitle}>Your transaction has been completed successfully.</div>
        </header>

        <section style={styles.summary}>
          <div>
            <div style={styles.smallLabel}>You Paid</div>
            <div style={styles.paidAmount}>{paidAmount}</div>
            <div style={styles.flagBadge}><CurrencyFlag currency={paidCurrency} src={paidFlagSrc} /> {paidCurrency}</div>
          </div>
          <div style={styles.arrow}><SvgIcon name="arrow" size={22} /></div>
          <div style={{ textAlign: "right" }}>
            <div style={styles.smallLabel}>You Received</div>
            <div style={styles.receivedAmount}>{receivedAmount}</div>
            <div style={styles.flagBadge}><CurrencyFlag currency={receivedCurrency} src={receivedFlagSrc} /> {receivedCurrency}</div>
          </div>
        </section>

        <section style={styles.card}>
          <div style={styles.title}><SvgIcon name="doc" /> Transaction Details</div>
          <DetailRow icon="doc" label="Reference ID" value={referenceId} copyValue={referenceId} />
          <DetailRow icon="tx" label="Transaction ID" value={transactionId} copyValue={transactionId} />
          <DetailRow icon="date" label="Date & Time" value={`${date} | ${time}`} />
          <DetailRow icon="user" label="Customer Name" value={customerName} />
          <DetailRow icon="card" label="Payment Method"><>{paymentMethod}<PaymentBrandIcon method={paymentMethod} /></></DetailRow>
          <DetailRow icon="rate" label="Exchange Rate" value={exchangeRate} />
          <DetailRow icon="money" label="Service Fee" value={serviceFee} />
          <DetailRow icon="money" label="Total Paid" value={totalPaid} last />
        </section>

        <section style={styles.highlight}>
          <div style={styles.highlightLabel}>Total Received</div>
          <div style={styles.highlightAmount}>{receivedAmount}</div>
          <div style={styles.thanks}><SvgIcon name="heart" size={16} /> Thank you for using Cavelen XDL</div>
        </section>

        <section style={styles.verify}>
          <div style={styles.qrBox}>
            {qrSrc && <img src={qrSrc} crossOrigin="anonymous" alt="Receipt verification QR code" style={{ width: 98, height: 98, borderRadius: 8 }} />}
          </div>
          <div>
            <div style={styles.verifyTitle}>Verify this receipt</div>
            <div style={styles.verifyText}>Scan the QR code to verify this transaction on Cavelen.</div>
            <div style={styles.verified}><SvgIcon name="shield" size={16} /> Verified by Cavelen</div>
          </div>
        </section>

        <div style={styles.trust}>
          <SvgIcon name="shield" size={15} /> Secure &nbsp; • &nbsp; Trusted &nbsp; • &nbsp; Reliable
          <div style={styles.partner}>Cavelen XDL - Your Trusted Exchange Partner</div>
        </div>
      </div>

      <footer style={styles.footer}>
        <div><SvgIcon name="lock" size={14} /> This is a system generated receipt.</div>
        <div>© {new Date().getFullYear()} Cavelen XDL. All rights reserved.</div>
      </footer>
    </article>
  );
}
